import { useCallback } from 'react';
import api from '../services/api';

/**
 * Hook para gerenciar registro de PUSH subscriptions
 * Permite registrar push notifications para qualquer evento
 * 
 * Uso:
 * const { registerPushSubscription } = usePushSubscription();
 * await registerPushSubscription('queue-123', 'https://...');
 */
export function usePushSubscription() {
  /**
   * Registra uma subscription de push para um identificador específico
   * @param identifyNotification ID único para a notificação (ex: 'queue-123')
   * @param url URL para onde redirecionar quando notificação é clicada
   * @returns true se registrou com sucesso, false caso contrário
   */
  const registerPushSubscription = useCallback(async (
    identifyNotification: string,
    url: string,
  ): Promise<boolean> => {
    try {
      console.log('[usePushSubscription] Registrando subscription:', {
        identifyNotification,
        url,
      });

      // 1. Buscar subscription armazenada localmente
      const subscriptionStorage = localStorage.getItem(
        window.btoa('subscription_notification')
      );

      if (!subscriptionStorage) {
        console.warn('[usePushSubscription] ⚠️ Nenhuma subscription encontrada no localStorage');
        return false;
      }

      let subscription = null;
      try {
        subscription = JSON.parse(window.atob(subscriptionStorage));
      } catch (e) {
        console.error('[usePushSubscription] Erro ao decodificar subscription:', e);
        return false;
      }

      if (!subscription || !subscription.endpoint) {
        console.warn('[usePushSubscription] ⚠️ Subscription inválida:', subscription);
        return false;
      }

      // 2. Enviar para backend
      const response = await api.post(
        '/api/notifications/register-subscription',
        {
          identifyNotification,
          subscription,
          url,
        }
      );

      if (response.data && response.data.success) {
        console.log('[usePushSubscription] ✅ Subscription registrada com sucesso:', {
          identifyNotification,
          endpoint: subscription.endpoint.substring(0, 50) + '...',
        });
        return true;
      } else {
        console.warn('[usePushSubscription] ⚠️ Resposta do backend sem sucesso:', response.data);
        return false;
      }
    } catch (error: any) {
      console.error('[usePushSubscription] ❌ Erro ao registrar subscription:', {
        error: error.message,
        identifyNotification,
      });
      // Não falhar silenciosamente - polling funcionará como fallback
      return false;
    }
  }, []);

  /**
   * Solicita permissão de notificação push ao usuário
   */
  const requestNotificationPermission = useCallback(async (): Promise<boolean> => {
    try {
      if (!('Notification' in window)) {
        console.warn('[usePushSubscription] Navegador não suporta notificações');
        return false;
      }

      if (Notification.permission === 'granted') {
        console.log('[usePushSubscription] Permissão de notificação já concedida');
        return true;
      }

      if (Notification.permission !== 'denied') {
        const permission = await Notification.requestPermission();
        return permission === 'granted';
      }

      return false;
    } catch (error) {
      console.warn('[usePushSubscription] Erro ao solicitar permissão:', error);
      return false;
    }
  }, []);

  /**
   * Verifica se o navegador suporta push notifications
   */
  const isPushSupported = useCallback((): boolean => {
    return 'serviceWorker' in navigator && 'PushManager' in window;
  }, []);

  return {
    registerPushSubscription,
    requestNotificationPermission,
    isPushSupported,
  };
}
